import { CSSProperties, forwardRef, Ref } from 'react';
import { AriaTextFieldProps } from 'react-aria';
import { Control, FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import TextAreaField from '~/components/inputs/TextField/TextAreaField';
import TextField, { TextFieldProps } from '~/components/inputs/TextField/TextField';

interface Props<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
  TTransformedValues = T,
> {
  autoComplete?: TextFieldProps['autoComplete'];
  autoFocus?: boolean;
  className?: string;
  control: Control<T, null, TTransformedValues>;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  hideLabel?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onClear?: () => void;
  inputClassName?: string;
  label: string;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  textAreaStyle?: CSSProperties;
  name: TName;
  required?: boolean;
  rules?: UseControllerProps<T, TName, TTransformedValues>['rules'];
  size?: TextFieldProps['size'];
  style?: CSSProperties;
  type?: React.HTMLInputTypeAttribute;
  forceIgnore1Password?: boolean;
  onKeyUp?: AriaTextFieldProps['onKeyUp'];
  onKeyDown?: AriaTextFieldProps['onKeyDown'];
  isReadOnly?: boolean;
}

interface WithForwardRefTypeProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
  TTransformedValues = T,
> extends React.FC<Props<T, TName, TTransformedValues>> {
  <T extends FieldValues = FieldValues, TName extends FieldPath<T> = FieldPath<T>, TTransformedValues = T>(
    props: Props<T, TName, TTransformedValues>,
  ): ReturnType<React.FC<Props<T, TName, TTransformedValues>>>;
}
const TextController: WithForwardRefTypeProps = forwardRef(
  (
    {
      autoComplete,
      autoFocus,
      className,
      control,
      disabled,
      helperText,
      hideLabel,
      icon,
      onClear,
      inputClassName,
      label,
      maxLength,
      multiline,
      rows,
      textAreaStyle,
      name,
      required,
      placeholder,
      rules,
      size,
      style,
      type,
      forceIgnore1Password,
      onKeyUp,
      onKeyDown,
      isReadOnly,
    },
    ref?: Ref<HTMLDivElement>,
  ) => {
    const { field, fieldState } = useController({ name, control, rules });

    const handleOnClear = () => {
      field.onChange('');
      onClear?.();
    };

    if (multiline) {
      return (
        <TextAreaField
          {...field}
          ref={ref}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className={className}
          hideLabel={hideLabel}
          label={label}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={fieldState.invalid}
          errorMessage={fieldState.error?.message}
          helpText={helperText}
          rows={rows}
          TextAreaProps={{ maxLength, className: inputClassName, style: textAreaStyle }}
          size={size}
          style={style}
        />
      );
    }

    return (
      <TextField
        {...field}
        ref={ref}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className={className}
        hideLabel={hideLabel}
        Icon={icon}
        onClear={onClear ? handleOnClear : undefined}
        placeholder={placeholder}
        isDisabled={disabled}
        label={label}
        isRequired={required}
        isInvalid={fieldState.invalid}
        errorMessage={fieldState.error?.message}
        helpText={helperText}
        InputProps={{ maxLength, className: inputClassName, type }}
        size={size}
        style={style}
        forceIgnore1Password={forceIgnore1Password}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        isReadOnly={isReadOnly}
      />
    );
  },
);

export default TextController;
